import { IrrigationRateHistoryEntry } from '../../../models/field-link';

/**
 * Text and numbers for the link editor's irrigation rate tab. A rate is mm per hour of iGator run
 * time; the sync service accepts above 0 and at most 100, kept to 3 decimals.
 */
export const MAX_MM_PER_HOUR = 100;

/** What the user typed: null when empty, 'invalid' when not a usable rate. Accepts 8,77 and 8.77. */
export function parseRateText(text: string | null | undefined): number | null | 'invalid' {
  const t = (text ?? '').replace(/\s/g, '');
  if (t === '') return null;
  if (!/^\d+([.,]\d+)?$/.test(t)) return 'invalid';
  const value = Math.round(Number(t.replace(',', '.')) * 1000) / 1000;
  if (!isFinite(value) || value <= 0 || value > MAX_MM_PER_HOUR) return 'invalid';
  return value;
}

/** For an input box: up to 3 decimals, no trailing zeros, decimal comma. 8.77 gives "8,77". */
export function rateInputText(mmPerHour: number | null | undefined): string {
  return mmPerHour == null ? '' : String(Number(mmPerHour.toFixed(3))).replace('.', ',');
}

/** For labels: 2 decimals, decimal comma. 8.3333 gives "8,33". */
export function rateLabel(mmPerHour: number): string {
  return mmPerHour.toFixed(2).replace('.', ',');
}

/** mm put on a field by a run of this many minutes at this rate. */
export function mmFor(minutes: number, mmPerHour: number): number {
  return minutes / 60 * mmPerHour;
}

export function mmLabel(mm: number): string {
  return mm.toFixed(2).replace('.', ',');
}

/** "Up 2,6 %", "Down 1,2 %" or "No change", for the farm rate confirm step. */
export function percentChangeLabel(from: number, to: number): string {
  const pct = (to / from - 1) * 100;
  const rounded = Math.round(pct * 10) / 10;
  if (rounded === 0) return 'No change';
  return `${rounded > 0 ? 'Up' : 'Down'} ${Math.abs(rounded).toFixed(1).replace('.', ',')} %`;
}

/** One line of the change history, after "date, user:". */
export function describeRateChange(h: IrrigationRateHistoryEntry): string {
  const what = h.scope === 'FARM' ? 'the farm rate' : "this field's own rate";
  if (h.newMmPerHour == null) {
    return h.scope === 'FARM' ? 'removed the farm rate' : "removed this field's own rate (uses the farm rate)";
  }
  const was = h.oldMmPerHour == null ? '' : ` (was ${rateLabel(h.oldMmPerHour)})`;
  return `set ${what} to ${rateLabel(h.newMmPerHour)} mm/h${was}`;
}
